/**
 * PDF Report Service
 * Handles generated PDF report operations
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { PDF_REPORT_ENDPOINTS, buildQueryString } from '../endpoints';

const pdfReportService = {
  /**
   * Get all generated reports (paginated, admin only)
   * @param {Object} params - Query params (search, status, formSchemaId, submissionId, page, pageSize, sortField, sortDirection)
   * @returns {Promise<Object>} PagedResult of report DTOs
   */
  getAllReports: async (params = {}) => {
    return apiRequest(() =>
      axiosInstance.get(`${PDF_REPORT_ENDPOINTS.LIST}${buildQueryString(params)}`)
    );
  },

  /**
   * Delete a generated report (admin only)
   * @param {number} reportId - Report ID
   * @returns {Promise<Object>} Success response
   */
  deleteReport: async (reportId) => {
    return apiRequest(() =>
      axiosInstance.delete(PDF_REPORT_ENDPOINTS.DELETE(reportId))
    );
  },

  /**
   * Get all generated reports for a submission
   * @param {number} submissionId - Submission ID
   * @returns {Promise<Array>} List of report DTOs
   */
  getReportsForSubmission: async (submissionId) => {
    return apiRequest(() =>
      axiosInstance.get(PDF_REPORT_ENDPOINTS.FOR_SUBMISSION(submissionId))
    );
  },

  /**
   * Get a specific report by ID
   * @param {number} reportId - Report ID
   * @returns {Promise<Object>} Report DTO
   */
  getReportById: async (reportId) => {
    return apiRequest(() =>
      axiosInstance.get(PDF_REPORT_ENDPOINTS.GET_BY_ID(reportId))
    );
  },

  /**
   * Get signed download URL for a completed report
   * @param {number} reportId - Report ID
   * @returns {Promise<Object>} { downloadUrl, expiresAt, fileName, fileSize, contentType }
   */
  getDownloadUrl: async (reportId) => {
    return apiRequest(() =>
      axiosInstance.get(PDF_REPORT_ENDPOINTS.DOWNLOAD(reportId))
    );
  },

  /**
   * Manually trigger PDF generation
   * @param {number} submissionId - Submission ID
   * @param {number} pdfTemplateId - PDF Template ID
   * @returns {Promise<Object>} Created report DTO
   */
  generateReport: async (submissionId, pdfTemplateId) => {
    return apiRequest(() =>
      axiosInstance.post(PDF_REPORT_ENDPOINTS.GENERATE, {
        submissionId,
        pdfTemplateId
      })
    );
  },

  /**
   * Regenerate a failed report
   * @param {number} reportId - Report ID
   * @returns {Promise<Object>} Updated report DTO
   */
  regenerateReport: async (reportId) => {
    return apiRequest(() =>
      axiosInstance.post(PDF_REPORT_ENDPOINTS.REGENERATE(reportId))
    );
  },

  /**
   * Get public share URL for a completed report
   * @param {number} reportId - Report ID
   * @returns {Promise<Object>} { publicUrl }
   */
  getPublicUrl: async (reportId) => {
    return apiRequest(() =>
      axiosInstance.get(PDF_REPORT_ENDPOINTS.PUBLIC_URL(reportId))
    );
  },

  /**
   * Generate a preview PDF without creating a DB record
   * @param {number} pdfTemplateId - PDF Template ID
   * @param {number|null} submissionId - Submission ID (null = AI sample data)
   * @param {string|null} timezone - IANA timezone (falls back to user's)
   * @returns {Promise<string>} Blob URL for the generated PDF
   */
  generatePreview: async (pdfTemplateId, submissionId = null, timezone = null) => {
    const response = await axiosInstance.post(
      PDF_REPORT_ENDPOINTS.PREVIEW,
      { pdfTemplateId, submissionId, timezone },
      { responseType: 'blob', timeout: 90000 }
    );
    return URL.createObjectURL(response.data);
  },

  /**
   * Share a report via email
   * @param {number} reportId - Report ID
   * @param {string} recipientEmail - Recipient email address
   * @param {string|null} note - Optional note
   * @returns {Promise<Object>} Success response
   */
  shareReport: async (reportId, recipientEmail, note) => {
    return apiRequest(() =>
      axiosInstance.post(PDF_REPORT_ENDPOINTS.SHARE(reportId), {
        recipientEmail,
        note: note || null,
      })
    );
  },
};

export default pdfReportService;
